'use client';

import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUtils } from '@mui/x-date-pickers/internals/hooks/useUtils';
import { usePickersTranslations } from '@mui/x-date-pickers/hooks/usePickersTranslations';
import { PickersArrowSwitcher } from '@mui/x-date-pickers/internals/components/PickersArrowSwitcher';
import { useNextMonthDisabled, usePreviousMonthDisabled } from '@mui/x-date-pickers/internals/hooks/date-helpers-hooks';

import { RADIUS, SHADOW } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify/iconify';

// ----------------------------------------------------------------------

type Day = dayjs.Dayjs

function clampDateToBounds(d: Day | null | undefined, min: Day, max: Day) {
  if (!d) return d
  if (d.isBefore(min)) return min
  if (d.isAfter(max)) return max
  return d
}

function clampPeriodRange(start: Day | null | undefined, end: Day | null | undefined, min: Day | null, max: Day | null) {
  if (!min || !max) return [start, end]
  const s = start ? clampDateToBounds(start, min, max) : start
  const e = end ? clampDateToBounds(end, min, max) : end
  if (s && e && s.isAfter(e)) return [s, s]
  return [s, e]
}

/**
 * 기간 선택을 위한 날짜 범위 선택 컴포넌트
 *
 * ⚠️ 구조분해 파라미터는 `props.<이름>` 형태로 적어야 한다. 루트 `@param {Object} props`가
 * 없으면 TS가 **첫 `@param`의 타입을 props 전체로** 읽어(여기서는 `number`) `.tsx`
 * 호출부가 아무 prop도 못 넘긴다 (§2.5).
 *
 * @param {Object} props
 * @param {any[]} [props.value]  `[시작, 끝]`
 * @param {(range: any[]) => void} [props.onChange]
 * @param {boolean} [props.hideTrigger]  트리거 버튼 없이 팝오버만 (열림은 `open`이 정한다)
 * @param {boolean} [props.open]
 * @param {any} [props.anchorEl]
 * @param {Function} [props.onClose]
 * @param {number} [props.restrictToRecentDays]  오늘 포함 최근 N일 안에서만 선택 (예: 90 → 오늘부터 90일 전 시작일까지)
 */
type CalendarHeaderProps = {
  slots?: unknown
  slotProps?: unknown
  onMonthChange: (month: Day, direction: 'left' | 'right') => void
  minDate?: Day
  maxDate?: Day
  disableFuture?: boolean
  disablePast?: boolean
  timezone?: string
  className?: string
  sx?: unknown
  yearRangePadding?: number
}

function PeriodRangePicker({
  value,
  onChange,
  hideTrigger = false,
  open,
  anchorEl: controlledAnchorEl,
  onClose,
  restrictToRecentDays,
}: {
  value: (Day | null)[]
  onChange: (range: (Day | null | undefined)[]) => void
  hideTrigger?: boolean
  open?: boolean
  anchorEl?: unknown
  onClose?: () => void
  restrictToRecentDays?: number
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const smUp = useMediaQuery('(min-width: 600px)')

  const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)
  const handleClose = () => {

    setAnchorEl(null)
    onClose?.()
  }

  // Dialog를 사용할 때는 open prop만 사용
  const effectiveOpen = open !== undefined ? open : !!(controlledAnchorEl ?? anchorEl)

  const pickerBounds =
    restrictToRecentDays && effectiveOpen
      ? {
          min: dayjs().subtract(restrictToRecentDays - 1, 'day').startOf('day'),
          max: dayjs().endOf('day'),
        }
      : { min: null, max: null }
  const { min: pickerMin, max: pickerMax } = pickerBounds

  // 내부 임시 값: 적용 버튼을 누르기 전까지 외부 onChange 호출하지 않음
  const [localStartDate, setLocalStartDate] = useState<Day | null | undefined>(value?.[0])
  const [localEndDate, setLocalEndDate] = useState<Day | null | undefined>(value?.[1])

  // 각 캘린더의 월을 추적 (화살표 클릭 시 월 변경을 반영하기 위해)
  // 초기값: 시작일과 종료일의 월
  const [startCalendarMonth, setStartCalendarMonth] = useState(() =>
    value?.[0] ? dayjs(value[0]).startOf('month') : dayjs().startOf('month')
  )
  const [endCalendarMonth, setEndCalendarMonth] = useState(() =>
    value?.[1] ? dayjs(value[1]).startOf('month') : dayjs().startOf('month')
  )

  // 다이얼로그가 열릴 때마다, 또는 외부 값이 변경되었을 때 내부값을 동기화
  useEffect(() => {
    if (value && Array.isArray(value) && value.length >= 2) {
      let nextStart: Day | null | undefined = value[0]
      let nextEnd: Day | null | undefined = value[1]
      if (restrictToRecentDays && effectiveOpen) {
        const max = dayjs().endOf('day')
        const min = dayjs().subtract(restrictToRecentDays - 1, 'day').startOf('day')
        ;[nextStart, nextEnd] = clampPeriodRange(nextStart, nextEnd, min, max)
      }
      setLocalStartDate(nextStart)
      setLocalEndDate(nextEnd)
      if (nextStart) setStartCalendarMonth(dayjs(nextStart).startOf('month'))
      if (nextEnd) setEndCalendarMonth(dayjs(nextEnd).startOf('month'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOpen, restrictToRecentDays, value?.[0]?.valueOf?.(), value?.[1]?.valueOf?.()])

  // 헤더 컴포넌트를 내부에 정의 (시작일용)
  const StartCalendarHeader = React.forwardRef<HTMLDivElement, CalendarHeaderProps>((props, ref) => {
    const {
      slots,
      slotProps,
      onMonthChange,
      minDate,
      maxDate,
      disableFuture,
      disablePast,
      timezone,
      className,
      sx,
      yearRangePadding = 10,
    } = props

    const translations = usePickersTranslations()
    const utils = useUtils()

    // 시작일 캘린더의 월 사용
    const effectiveMonth = startCalendarMonth
    const current = effectiveMonth
    const currentYear = current.year()
    const currentMonthIndex = current.month()

    // onMonthChange를 래핑하여 startCalendarMonth도 업데이트
    const wrappedOnMonthChange = (newMonth: Day, direction: 'left' | 'right') => {
      const newMonthStart = dayjs(newMonth).startOf('month')
      setStartCalendarMonth(newMonthStart)
      onMonthChange(newMonth, direction)
    }

    const parsedMinYear = dayjs(minDate ?? current).year()
    const parsedMaxYear = dayjs(maxDate ?? current).year()
    const startYear = Math.max(parsedMinYear, currentYear - yearRangePadding)
    const endYear = Math.min(parsedMaxYear, currentYear + yearRangePadding)

    const yearOptions = []
    for (let year = startYear; year <= endYear; year += 1) {
      yearOptions.push(year)
    }
    if (!yearOptions.length) {
      yearOptions.push(currentYear)
    }

    const monthOptions = Array.from({ length: 12 }, (_, index) => ({
      value: index,
      label: `${index + 1}월`,
    }))

    const createUpdatedMonth = (nextYear = currentYear, nextMonth = currentMonthIndex) => (
      dayjs(effectiveMonth).date(1).year(nextYear).month(nextMonth)
    )

    const handleYearChange = (event: { stopPropagation?: () => void; target: { value: unknown } }) => {
      event?.stopPropagation?.()
      const nextYear = Number(event.target.value)
      if (Number.isNaN(nextYear) || nextYear === currentYear) return
      const direction = nextYear > currentYear ? 'left' : 'right'
      const newMonth = createUpdatedMonth(nextYear, currentMonthIndex)
      wrappedOnMonthChange(newMonth, direction)
    }

    const handleMonthSelectChange = (event: { stopPropagation?: () => void; target: { value: unknown } }) => {
      event?.stopPropagation?.()
      const nextMonth = Number(event.target.value)
      if (Number.isNaN(nextMonth) || nextMonth === currentMonthIndex) return
      const direction = nextMonth > currentMonthIndex ? 'left' : 'right'
      const newMonth = createUpdatedMonth(currentYear, nextMonth)
      wrappedOnMonthChange(newMonth, direction)
    }

    const selectNextMonth = () => wrappedOnMonthChange(utils.addMonths(effectiveMonth, 1), 'left')
    const selectPreviousMonth = () => wrappedOnMonthChange(utils.addMonths(effectiveMonth, -1), 'right')

    const isNextMonthDisabled = useNextMonthDisabled(effectiveMonth, { disableFuture, maxDate, timezone } as Parameters<typeof useNextMonthDisabled>[1])
    const isPreviousMonthDisabled = usePreviousMonthDisabled(effectiveMonth, { disablePast, minDate, timezone } as Parameters<typeof usePreviousMonthDisabled>[1])

    return (
      <Stack
        ref={ref}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        className={className}
        sx={[
          { px: 2, pt: 1, pb: 0.5 },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ] as React.ComponentProps<typeof Stack>['sx']}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Select
            size="small"
            value={currentYear}
            onChange={handleYearChange}
            MenuProps={{ disablePortal: true }}
            sx={{ minWidth: 96, '& .MuiSelect-select': { py: 0.5 } }}
          >
            {yearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {`${year}년`}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={currentMonthIndex}
            onChange={handleMonthSelectChange}
            MenuProps={{ disablePortal: true }}
            sx={{ minWidth: 88, '& .MuiSelect-select': { py: 0.5 } }}
          >
            {monthOptions.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <PickersArrowSwitcher
          slots={slots as React.ComponentProps<typeof PickersArrowSwitcher>['slots']}
          slotProps={slotProps as React.ComponentProps<typeof PickersArrowSwitcher>['slotProps']}
          onGoToPrevious={selectPreviousMonth}
          isPreviousDisabled={isPreviousMonthDisabled}
          previousLabel={translations.previousMonth}
          onGoToNext={selectNextMonth}
          isNextDisabled={isNextMonthDisabled}
          nextLabel={translations.nextMonth}
        />
      </Stack>
    )
  })

  // 헤더 컴포넌트를 내부에 정의 (종료일용)
  const EndCalendarHeader = React.forwardRef<HTMLDivElement, CalendarHeaderProps>((props, ref) => {
    const {
      slots,
      slotProps,
      onMonthChange,
      minDate,
      maxDate,
      disableFuture,
      disablePast,
      timezone,
      className,
      sx,
      yearRangePadding = 10,
    } = props

    const translations = usePickersTranslations()
    const utils = useUtils()

    // 종료일 캘린더의 월 사용
    const effectiveMonth = endCalendarMonth
    const current = effectiveMonth
    const currentYear = current.year()
    const currentMonthIndex = current.month()

    // onMonthChange를 래핑하여 endCalendarMonth도 업데이트
    const wrappedOnMonthChange = (newMonth: Day, direction: 'left' | 'right') => {
      const newMonthStart = dayjs(newMonth).startOf('month')
      setEndCalendarMonth(newMonthStart)
      onMonthChange(newMonth, direction)
    }

    const parsedMinYear = dayjs(minDate ?? current).year()
    const parsedMaxYear = dayjs(maxDate ?? current).year()
    const startYear = Math.max(parsedMinYear, currentYear - yearRangePadding)
    const endYear = Math.min(parsedMaxYear, currentYear + yearRangePadding)

    const yearOptions = []
    for (let year = startYear; year <= endYear; year += 1) {
      yearOptions.push(year)
    }
    if (!yearOptions.length) {
      yearOptions.push(currentYear)
    }

    const monthOptions = Array.from({ length: 12 }, (_, index) => ({
      value: index,
      label: `${index + 1}월`,
    }))

    const createUpdatedMonth = (nextYear = currentYear, nextMonth = currentMonthIndex) => (
      dayjs(effectiveMonth).date(1).year(nextYear).month(nextMonth)
    )

    const handleYearChange = (event: { stopPropagation?: () => void; target: { value: unknown } }) => {
      event?.stopPropagation?.()
      const nextYear = Number(event.target.value)
      if (Number.isNaN(nextYear) || nextYear === currentYear) return
      const direction = nextYear > currentYear ? 'left' : 'right'
      const newMonth = createUpdatedMonth(nextYear, currentMonthIndex)
      wrappedOnMonthChange(newMonth, direction)
    }

    const handleMonthSelectChange = (event: { stopPropagation?: () => void; target: { value: unknown } }) => {
      event?.stopPropagation?.()
      const nextMonth = Number(event.target.value)
      if (Number.isNaN(nextMonth) || nextMonth === currentMonthIndex) return
      const direction = nextMonth > currentMonthIndex ? 'left' : 'right'
      const newMonth = createUpdatedMonth(currentYear, nextMonth)
      wrappedOnMonthChange(newMonth, direction)
    }

    const selectNextMonth = () => wrappedOnMonthChange(utils.addMonths(effectiveMonth, 1), 'left')
    const selectPreviousMonth = () => wrappedOnMonthChange(utils.addMonths(effectiveMonth, -1), 'right')

    const isNextMonthDisabled = useNextMonthDisabled(effectiveMonth, { disableFuture, maxDate, timezone } as Parameters<typeof useNextMonthDisabled>[1])
    const isPreviousMonthDisabled = usePreviousMonthDisabled(effectiveMonth, { disablePast, minDate, timezone } as Parameters<typeof usePreviousMonthDisabled>[1])

    return (
      <Stack
        ref={ref}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        className={className}
        sx={[
          { px: 2, pt: 1, pb: 0.5 },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ] as React.ComponentProps<typeof Stack>['sx']}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Select
            size="small"
            value={currentYear}
            onChange={handleYearChange}
            MenuProps={{ disablePortal: true }}
            sx={{ minWidth: 96, '& .MuiSelect-select': { py: 0.5 } }}
          >
            {yearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {`${year}년`}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={currentMonthIndex}
            onChange={handleMonthSelectChange}
            MenuProps={{ disablePortal: true }}
            sx={{ minWidth: 88, '& .MuiSelect-select': { py: 0.5 } }}
          >
            {monthOptions.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <PickersArrowSwitcher
          slots={slots as React.ComponentProps<typeof PickersArrowSwitcher>['slots']}
          slotProps={slotProps as React.ComponentProps<typeof PickersArrowSwitcher>['slotProps']}
          onGoToPrevious={selectPreviousMonth}
          isPreviousDisabled={isPreviousMonthDisabled}
          previousLabel={translations.previousMonth}
          onGoToNext={selectNextMonth}
          isNextDisabled={isNextMonthDisabled}
          nextLabel={translations.nextMonth}
        />
      </Stack>
    )
  })

  return (
    <>
      {!hideTrigger && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ height: '30px', cursor: 'pointer', borderRadius: RADIUS.sm, px: 1, py: 0.3, bgcolor: 'white' }} onClick={handleClick}>
          <Typography variant="body2" color="common.black">
            {/*
              ⚠️ **조건 칩과 같은 글자여야 한다**(`YYYY.MM.DD`). 고른 결과를 적는 칩
                (`filter-period-slot`)이 네 자리 해를 쓰므로 여기도 같아야 한다 — 같은 날짜를
                두 모양으로 보여 주면 잘못 골랐나 싶어진다.
            */}
            {`${value[0]?.format('YYYY.MM.DD')} ~ ${value[1]?.format('YYYY.MM.DD')}`}
          </Typography>
          <Iconify icon="solar:calendar-linear" sx={{ color: 'white', width: 14, height: 14 }} />
        </Stack>
      )}
      <Dialog
        open={effectiveOpen}
        onClose={(event) => { try { (event as { stopPropagation?: () => void } | undefined)?.stopPropagation?.() } catch { /* no-op */ } handleClose() }}
        maxWidth={false}
        disableEscapeKeyDown={false}
        sx={{
          '& .MuiDialog-paper': {
            border: 'none',
            borderRadius: RADIUS.lg,
            bgcolor: 'white',
            py: { xs: '16px', sm: '24px' },
            px: { xs: '0px', sm: '24px' },
            display: 'flex',
            flexDirection: 'column',
            minWidth: { xs: 320, sm: 400, md: 600 },
            boxShadow: SHADOW.overlay,
          }
        }}
      >
        <ThemeProvider theme={createTheme({ palette: { mode: 'light' } })}>
          <Box sx={{ position: 'relative' }}>
            <IconButton
              aria-label="닫기"
              onClick={(event) => { try { event?.stopPropagation?.() } catch { /* no-op */ } handleClose() }}
              sx={{ position: 'absolute', top: -8, right: { xs: 8, sm: -8 }, zIndex: 1, color: 'text.secondary' }}
            >
              <Iconify icon="mingcute:close-line" width={20} />
            </IconButton>

            {/* 가로 슬라이드 허용 컨테이너 (작은 화면에서 잘리는 경우 수평 스크롤) */}
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Box sx={{
                minWidth: smUp ? 600 : 280,
                display: 'flex',
                mt: { xs: 2, sm: 0 },
                flexDirection: smUp ? 'row' : 'column',
                gap: smUp ? 2 : 0,
              }}>
                {/* Shortcuts (왼쪽에 배치) */}
                {smUp && (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    minWidth: 120,
                    pt: 1,
                  }}>
                    {[
                      { label: '이번 주', getValue: () => [dayjs().startOf('week'), dayjs()] },
                      { label: '이번 달', getValue: () => [dayjs().startOf('month'), dayjs()] },
                      { label: '최근 7일', getValue: () => [dayjs().subtract(7, 'day'), dayjs()] },
                      { label: '최근 14일', getValue: () => [dayjs().subtract(14, 'day'), dayjs()] },
                      { label: '최근 30일', getValue: () => [dayjs().subtract(30, 'day'), dayjs()] },
                      { label: '최근 90일', getValue: () => [dayjs().subtract(90, 'day'), dayjs()] },
                    ].map((shortcut) => (
                      <Button
                        key={shortcut.label}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          let [start, end]: (Day | null | undefined)[] = shortcut.getValue()
                          if (pickerMin && pickerMax) {
                            ;[start, end] = clampPeriodRange(start, end, pickerMin, pickerMax)
                          }
                          setLocalStartDate(start)
                          setLocalEndDate(end)
                          setStartCalendarMonth((start as Day).startOf('month'))
                          setEndCalendarMonth((end as Day).startOf('month'))
                        }}
                        sx={{
                          borderRadius: RADIUS.md,
                          borderColor: 'divider',
                          color: 'common.black',
                          bgcolor: 'white',
                          textTransform: 'none',
                          fontSize: '13.5px',
                          justifyContent: 'flex-start',
                          px: 1.5,
                          py: 1,
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50',
                          },
                        }}
                      >
                        {shortcut.label}
                      </Button>
                    ))}
                  </Box>
                )}

                {/* 시작일 캘린더 */}
                <Box sx={{ flex: 1, minWidth: smUp ? 320 : '100%' }}>
                  <DateCalendar
                    value={localStartDate}
                    onChange={(newValue) => {
                      setLocalStartDate(newValue)
                      if (newValue) setStartCalendarMonth(dayjs(newValue).startOf('month'))
                      // 종료일이 시작일보다 이전이면 종료일도 업데이트
                      if (newValue && localEndDate && dayjs(newValue).isAfter(localEndDate)) {
                        setLocalEndDate(newValue)
                        setEndCalendarMonth(dayjs(newValue).startOf('month'))
                      }
                    }}
                    referenceDate={startCalendarMonth}
                    minDate={pickerMin ?? undefined}
                    maxDate={pickerMax ?? undefined}
                    slots={{ calendarHeader: StartCalendarHeader }}
                    slotProps={{
                      calendarHeader: { yearRangePadding: 25 } as Record<string, unknown>,
                    }}
                  />
                </Box>

                {/* 종료일 캘린더 */}
                <Box sx={{ flex: 1, minWidth: smUp ? 320 : '100%' }}>
                  <DateCalendar
                    value={localEndDate}
                    onChange={(newValue) => {
                      setLocalEndDate(newValue)
                      if (newValue) setEndCalendarMonth(dayjs(newValue).startOf('month'))
                      // 시작일이 종료일보다 이후면 시작일도 업데이트
                      if (newValue && localStartDate && dayjs(newValue).isBefore(localStartDate)) {
                        setLocalStartDate(newValue)
                        setStartCalendarMonth(dayjs(newValue).startOf('month'))
                      }
                    }}
                    referenceDate={endCalendarMonth}
                    minDate={pickerMin ?? undefined}
                    maxDate={pickerMax ?? undefined}
                    slots={{ calendarHeader: EndCalendarHeader }}
                    slotProps={{
                      calendarHeader: { yearRangePadding: 25 } as Record<string, unknown>,
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={{ mt: 1.5, mx: { xs: 2, sm: 0 }, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={(event) => { try { event?.stopPropagation?.() } catch { /* no-op */ } handleClose() }}
                sx={{
                  borderRadius: RADIUS.lg,
                  borderColor: 'divider',
                  color: 'common.black',
                  bgcolor: 'white',
                  textTransform: 'none',
                  px: 1.5,
                  minHeight: 30,
                }}
              >
                취소
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={(event) => {
                  try { event?.stopPropagation?.() } catch { /* no-op */ }
                  let s = localStartDate
                  let e = localEndDate
                  if (pickerMin && pickerMax) {
                    ;[s, e] = clampPeriodRange(s, e, pickerMin, pickerMax)
                  }
                  onChange?.([s, e])
                  handleClose()
                }}
                sx={{
                  borderRadius: RADIUS.lg,
                  textTransform: 'none',
                  px: 1.5,
                  minHeight: 30,
                }}
              >
                적용
              </Button>
            </Box>
          </Box>
        </ThemeProvider>
      </Dialog>
    </>
  )
}

PeriodRangePicker.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  hideTrigger: PropTypes.bool,
  open: PropTypes.bool,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func,
  restrictToRecentDays: PropTypes.number,
}

export default PeriodRangePicker;


